import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Router } from '@angular/router';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreIndustriesComponent } from '../explore-more-industries/explore-more-industries.component';
import { trigger, transition, query, style, stagger, animate } from '@angular/animations';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface CaseStudy {
  image: string;
  title: string;
  description: string;
}
@Component({
  selector: 'app-media-entertainment',
  imports: [CommonModule, RouterModule, FaqComponent, ExploreMoreIndustriesComponent],
  standalone: true,
  templateUrl: './media-entertainment.component.html',
  styleUrl: './media-entertainment.component.scss',
  animations: [
    trigger('fadeInUpStagger', [
      transition('* => visible', [
        query('.animate-item', [
          style({ opacity: 0, transform: 'translateY(30px)' }),
          stagger('200ms', [
            animate('800ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class MediaEntertainmentComponent implements AfterViewInit, OnInit, OnDestroy {
  private jsonLdScript: any;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  @ViewChild('impactSection') impactSection!: ElementRef;
  impactSectionVisible = false;
  private observer: IntersectionObserver | null = null;

  caseStudies: CaseStudy[] = [
    {
      image: '../../../../assets/industries/entertainment/media1.png',
      title: 'Mobile Movie Ticketing App for Hassle-Free Cinema Experiences',
      description: 'Kalpita Technologies developed a cutting-edge mobile application for online movie ticket booking, revolutionizing the process by replacing manual queues with seamless digital access to theatres, movies, showtimes, and secure payments. The platform stands out with unique features like ticket rescheduling, cancellation up to one hour before screening, seat layouts, theatre previews, reminders, and multi-role dashboards for super admins, theatre operators, and end-users.'
    }
    // {
    //   image: '../../../../assets/industries/healthcare/case-study-healthcare.png',
    //   title: 'Online Retail E-commerce Platform',
    //   description: 'We developed a mobile ecommerce platform for a Chilean retail client, featuring three managed access roles, Admin, Seller, and Customer, alongside integrated payment gateways like F-Pay for seamless transactions. The app addresses multi-country operations across Chile, Peru, Colombia, and Argentina by enabling in-app location switching for Spanish-language, region-specific products and offers while maintaining a consistent interface. Firebase Crashlytics ensures real-time crash monitoring to prioritize user-impacting issues.'
    // }
    // {
    //   image: '../../../../assets/industries/healthcare/case-study-healthcare.png',
    //   title: 'Unified Customer Engagement Portal',
    //   description: 'Kalpita Technologies partnered with a top-tier insurance provider to modernize their customer engagement ecosystem through a unified digital portal. The platform consolidated multiple service lines into one secure, SOC2 and GDPR compliant environment, enabling seamless client–advisor collaboration, consistent brand communication, and actionable business insights via integrated Power BI dashboards. This transformation enhanced customer retention, improved decision-making, and reinforced the client’s brand identity across all insurance verticals.'
    // }
  ];
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  mediaFaqs: FaqItem[] = [
    {
      question: 'What services does Kalpita offer for Media & Entertainment companies?',
      answer: `We provide OTT platform development (web, iOS, Android, Smart TV), live streaming 
      app development with CDN integration, ticketing solutions, content management platforms, 
      cloud integration, QA automation, performance engineering, custom digital media solutions 
      and AI-powered content recommendations.`
    },
    {
      question: 'How long does it take to build an OTT streaming app?',
      answer: `Depending on features, content models, and integrations, it typically takes 12–20 weeks 
      for a fully functional MVP. We follow agile methodology with bi-weekly sprints and can deliver 
      a production-ready platform in phases.`
    },
    {
      question: 'Can you build a custom mobile ticketing application?',
      answer: `Yes. We design and develop secure, scalable mobile ticketing apps with seat selection, 
      QR-code entry, payment gateways, event dashboards, and analytics. Our ticketing solutions 
      integrate with popular payment gateways (Stripe, PayPal, etc.) and support features like 
      dynamic pricing, loyalty programs, and real-time seat availability.`
    },
    {
      question: 'Do you support large-scale live streaming applications?',
      answer: `Absolutely, our solutions handle high concurrency, CDN integration, low-latency streaming, 
      and adaptive bitrate to ensure smooth performance.`
    },
    {
      question: 'Are your solutions scalable for global audiences?',
      answer: `Yes, we build cloud-native, high-availability systems optimized for global reach, security, 
      and performance. Our solutions support multi-language, multi-currency, geo-blocking, and comply 
      with regional regulations.`
    },
    {
      question: 'What is AI-accelerated software development?',
      answer: `AI-accelerated development uses artificial intelligence to speed up architecture design, 
      code generation, testing, and integration - reducing development time by 50-60% while 
      maintaining high quality standards. This approach allows businesses to launch new capabilities 
      in weeks or months rather than years.`
    }
  ];

  currentSlide = 0;
  isDragging = false;
  startPosition = 0;
  currentPosition = 0;
  transformValue = 0;
  private autoSlideInterval: any;
  private readonly SLIDE_DURATION = 5000;

  constructor(private router: Router, private modalService: NgbModal,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    this.stopAutoSlide();
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    // 4. Set up Intersection Observer
    const options = {
      root: null,
      threshold: 0.2,
      rootMargin: '0px'
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.impactSectionVisible = true;
          this.observer?.disconnect();
        }
      });
    }, options);

    if (this.impactSection) {
      this.observer.observe(this.impactSection.nativeElement);
    }

    // 5. Initialize 3D Tilt Effect
    this.setupTiltEffect();
  }

  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.solution-image-card, .impact-image-wrapper, .case-study-image-wrapper');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xc = rect.width / 2;
        const yc = rect.height / 2;
        const maxRotate = 10;

        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  scheduleDemo(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  startAutoSlide(): void {
    this.stopAutoSlide(); // Ensure no duplicate intervals

    // MODIFIED: Only start auto-slide if there is more than 1 slide
    if (this.caseStudies.length > 1) {
      this.autoSlideInterval = setInterval(() => {
        this.nextSlide();
      }, this.SLIDE_DURATION);
    }
  }

  stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
  }

  nextSlide(): void {
    if (this.currentSlide < this.caseStudies.length - 1) {
      this.currentSlide++;
    } else {
      this.currentSlide = 0;
    }
  }

  goToSlide(index: number): void {
    if (this.caseStudies.length <= 1) return;
    this.currentSlide = index;
    this.stopAutoSlide();
    this.startAutoSlide();
  }

  onMouseEnter(): void {
    this.stopAutoSlide();
  }

  onMouseLeave(): void {
    this.startAutoSlide();
  }

  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.startPosition = event.clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  onTouchStart(event: TouchEvent): void {
    this.isDragging = true;
    this.startPosition = event.touches[0].clientX;
    this.currentPosition = this.startPosition;
    clearInterval(this.autoSlideInterval);
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.isDragging) return;
    this.currentPosition = event.touches[0].clientX;
    this.transformValue = -this.currentSlide * 100 + ((this.currentPosition - this.startPosition) / window.innerWidth * 100);
  }

  redirectToAbout(): void {
    this.router.navigate(['/about-us']);
  }

  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.startAutoSlide();
  }
  injectSchemaMarkup(): void {
    const mediaSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/industries/media-entertainment#webpage",
          "url": "https://www.kalpitatechnologies.com/industries/media-entertainment",
          "name": "Media & Entertainment Software Development | OTT, Ticketing & Streaming Solutions | Kalpita Technologies",
          "description": "Custom OTT platforms, live streaming apps, and mobile ticketing solutions by Kalpita Technologies. Scalable media software for global audiences.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en",
          "datePublished": "2026-01-22"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/industries/media-entertainment#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com" },
            { "@type": "ListItem", "position": 2, "name": "Industries", "item": "https://www.kalpitatechnologies.com/industries" },
            { "@type": "ListItem", "position": 3, "name": "Media & Entertainment", "item": "https://www.kalpitatechnologies.com/industries/media-entertainment" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/industries/media-entertainment#service",
          "name": "Media & Entertainment Software Development Solutions",
          "serviceType": "Media & Entertainment Software Development",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "End-to-end digital media and entertainment software solutions including OTT platform development, live streaming, and mobile ticketing."
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/industries/media-entertainment#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What services does Kalpita offer for Media & Entertainment companies?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We provide OTT platform development, live streaming app development with CDN integration, ticketing solutions, and content management platforms."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(mediaSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  onCardClick(index: number): void {
    console.log('Clicked card', index);
  }

  getStarArray(rating: number): boolean[] {
    return Array(5).fill(false).map((_, index) => index < rating);
  }

  testimonials(): void {
    this.router.navigate(['/'], { fragment: 'testimonials' });
  }
}
